import React, { useLayoutEffect, useMemo } from 'react';
import { Platform, Text, View } from 'react-native';
import LinearGradient from 'utils/packages/linearGradient';
import Animated, { Extrapolate, interpolate, interpolateColor, useAnimatedStyle, } from 'react-native-reanimated';
import normalize from 'common/Normalize';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useDefaultHeaderHeight, useStatusBarHeight, } from 'hooks/useHeaderHeight';
import useStyles from 'hooks/useStyles';
import { TAB_SCREEN_HEADER_HEIGHT } from 'navigators/styles';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { rem } from './values';
import { isIOS, isAndroid } from '../constants';
const ANDROID_TOP_PADDING = 7;
const IOS_TOP_PADDING = 2;
export const NavigationHeader = ({ children, scrollY, title, hideTitleOnScroll, renderTitle, leftContent, rightContent, style, absolute, outputColors, shadowInputRange = [0, 40], bgColorInputRange = [0, 40], headerColor, noPaddingTop, isIniOS13Modal, isInTab, skipSafeArea, regularTitleHeight, hasShadow = true, withFrame, isInModal, isWalkthroughActive, ...props }) => {
    const navigation = useMainStackNavigation();
    const headerHeight = useDefaultHeaderHeight();
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        if (!isInTab && !isInModal) {
            navigation.setOptions({
                ...(!!leftContent && {
                    headerLeft: () => leftContent,
                }),
                ...(!!rightContent && {
                    headerRight: () => rightContent,
                }),
            });
        }
    }, [isInModal, isInTab, leftContent, rightContent]);
    const headerBackground = useAnimatedStyle(() => ({
        shadowOpacity: interpolate(scrollY?.value ?? 0, shadowInputRange, [0, 1]),
    }));
    const mask = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY?.value ?? 0, bgColorInputRange, outputColors ||
            [colors.background.dark, colors.cards.onDark]),
    }));
    const androidShadowOpacity = useAnimatedStyle(() => ({
        opacity: interpolate(scrollY?.value ?? 0, shadowInputRange, [0, 1], Extrapolate.CLAMP),
    }));
    const titleContainer = useAnimatedStyle(() => ({
        opacity: interpolate(scrollY?.value ?? 0, bgColorInputRange, hideTitleOnScroll ? [0, 1] : [1, 1], Extrapolate.CLAMP),
    }));
    const hasTitle = !!(renderTitle || title);
    const top = useStatusBarHeight();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isInTab);
    const containerStyle = useMemo(() => {
        let height = headerHeight;
        if (noPaddingTop)
            height = 0;
        if (isIniOS13Modal)
            height = undefined;
        if (isInTab && !regularTitleHeight)
            height = TAB_SCREEN_HEADER_HEIGHT + top;
        let PLATFORM_TOP_PADDING = top;
        if (isIOS && !isInTab && hasTitle) {
            PLATFORM_TOP_PADDING = top + IOS_TOP_PADDING;
        }
        else if (isAndroid && !isInTab && hasTitle) {
            PLATFORM_TOP_PADDING = top - ANDROID_TOP_PADDING;
        }
        return [
            {
                height,
                backgroundColor: headerColor,
                paddingTop: skipSafeArea ? 0 : PLATFORM_TOP_PADDING,
            },
            styles.header,
        ];
    }, [
        headerHeight,
        noPaddingTop,
        isIniOS13Modal,
        isInTab,
        regularTitleHeight,
        top,
        hasTitle,
        headerColor,
        skipSafeArea,
        styles.header,
    ]);
    const titleContainerStyle = useMemo(() => {
        const out = [styles.titleContainer, titleContainer];
        if (!hasTitle)
            out.push({ flex: 0 });
        return out;
    }, [hasTitle, titleContainer, styles.titleContainer]);
    return (<Animated.View style={[
            styles.background,
            hasShadow ? headerBackground : styles.noShadow,
            absolute && styles.absolute,
            { backgroundColor: headerColor },
            style,
        ]} {...props}>
      <Animated.View style={[styles.mask, mask]}/>
      <View style={containerStyle}>
        {(!!isInTab || !!isInModal) && (<View style={styles.sideContainer}>{leftContent}</View>)}
        <Animated.View style={titleContainerStyle}>
          {renderTitle ? (renderTitle(styles.title)) : (<Text style={[
                styles.title,
                isWalkthroughActive && styles.walkthroughTitleStyles,
            ]}>
              {title}
            </Text>)}
        </Animated.View>
        {(!!isInTab || !!isInModal) && (<View style={styles.sideContainer}>{rightContent}</View>)}
      </View>
      <View style={{ backgroundColor: headerColor }}>
        <View style={withFrame ? paddingHorizontalStyle : null}>
          {children}
        </View>
        {Boolean(Platform.OS === 'android') && hasShadow ? (<Animated.View style={androidShadowOpacity}>
            <LinearGradient pointerEvents="none" style={styles.androidShadow} colors={[
                colors.shadows.androidShadowGradientStart,
                colors.shadows.androidShadowGradientEnd,
            ]}/>
          </Animated.View>) : null}
      </View>
      {props.overlay ? <props.overlay /> : null}
    </Animated.View>);
};
const styleSet = createStyleSheets((colors) => ({
    absolute: {
        position: 'absolute',
    },
    mask: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
    },
    header: {
        justifyContent: 'space-between',
        flexDirection: 'row',
        zIndex: 2,
    },
    background: {
        left: 0,
        right: 0,
        zIndex: 1,
        ...colors.shadows.card,
    },
    noShadow: {
        shadowOpacity: 0,
    },
    androidShadow: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 10,
    },
    titleContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    sideContainer: {
        flex: 1.1 / 3,
        justifyContent: 'center',
    },
    title: {
        color: colors.text.primary,
        fontFamily: 'Montserrat-Regular',
        fontSize: normalize(14.4),
        lineHeight: normalize(14.4) * 1.1,
    },
    walkthroughTitleStyles: {
        color: colors.elements.primaryOnModal,
    },
    titlePadded: {
        paddingVertical: rem(16),
    },
    topPaddingAndroid: {
        height: ANDROID_TOP_PADDING,
    },
    topPaddingIos: {
        marginTop: -IOS_TOP_PADDING,
    },
}));
